import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import StyledFormSection from '../styled/StyledFormSection';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import StyledInput from '../styled/StyledInput';
import { Label } from '../ui/label';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import StyledButton from '../styled/StyledButton';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import StyledTranslatableTextarea from '../styled/StyledTranslatableTextarea';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useMemo } from 'react';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';
import { useTranslateFields } from '@/hooks/useTranslate';
import TranslateIcon from '../translation/TranslateIcon';

interface OneWayFeesListProps {
  control: Control<CarRentalFormDataWithTranslations>;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const OneWayFeesList = ({
  control,
  language,
  isViewMode = false,
}: OneWayFeesListProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'oneWayFees',
  });
  const { append: appendTranslation, remove: removeTranslation } =
    useTranslationFieldArray({
      control,
      name: 'oneWayFees',
    });

  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();
  const { translateFields, isTranslating } = useTranslateFields({
    control: control,
    setValue,
    fields: [],
    nestedFields: {
      oneWayFees: ['name', 'description'],
    },
    targetLanguage: language,
  });

  return (
    <StyledFormSection
      sectionTitle={t('carRental.categoryForm.addOneWayFee')}
      icon={
        <TranslateIcon
          translateFields={() => translateFields({ allNestedFields: true })}
          isTranslating={isTranslating}
          language={language}
          hidden={isViewMode}
        />
      }
    >
      {fields.map((item, index) => (
        <OneWayFeesCard
          key={item.id}
          control={control}
          index={index}
          language={language}
          deleteOneWayFee={() => {
            remove(index);
            removeTranslation(index);
          }}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('carRental.categoryForm.addOneWayFee')}
          onClick={() => {
            append({
              departureCity: undefined,
              arrivalCity: undefined,
              description: undefined,
              amount: undefined,
            } as CarRentalFormDataWithTranslations['oneWayFees'][number]);
            appendTranslation(() => ({
              name: undefined,
              description: undefined,
            }));
          }}
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </StyledFormSection>
  );
};

export default OneWayFeesList;

interface OneWayFeesCardProps {
  control: Control<CarRentalFormDataWithTranslations>;
  index: number;
  language: SupportedLocale;
  deleteOneWayFee: () => void;
  isViewMode?: boolean;
}

const OneWayFeesCard = ({
  control,
  index,
  language,
  deleteOneWayFee,
  isViewMode = false,
}: OneWayFeesCardProps) => {
  const { t } = useTranslation();
  const currentTitle = useWatch({
    control,
    name: `oneWayFees.${index}.name`,
  });
  const translations = useWatch({
    control,
    name: 'translations',
  });
  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const cardTitle = useMemo(() => {
    const currentTranslation = translations?.[language]?.oneWayFees?.[index];
    const currentTitleTranslated = currentTranslation?.name ?? currentTitle;
    return (
      currentTitleTranslated ||
      currentTitle ||
      t('carRental.categoryForm.oneWayFee', { index: index + 1 })
    );
  }, [currentTitle, index, language, t, translations]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle className="text-base leading-6 hover:cursor-pointer">
          {cardTitle}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {index > 0 && !isViewMode && (
              <StyledTooltipContainer
                tooltipMessage={t('carRental.categoryForm.delete')}
              >
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={deleteOneWayFee}
                />
              </StyledTooltipContainer>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <StyledTranslatableInput
            originalName={`oneWayFees.${index}.name`}
            control={control}
            title={t('carRental.categoryForm.name')}
            name={`oneWayFees.${index}.name`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.categoryForm.namePlaceholder')}
            disabled={isViewMode}
          />
          <div className="flex flex-col md:flex-row gap-4">
            <StyledInput
              control={control}
              title={t('carRental.categoryForm.departureCity')}
              name={`oneWayFees.${index}.departureCity`}
              placeholder={t('carRental.categoryForm.departureCityPlaceholder')}
              disabled={isViewMode}
            />
            <StyledInput
              control={control}
              title={t('carRental.categoryForm.arrivalCity')}
              name={`oneWayFees.${index}.arrivalCity`}
              placeholder={t('carRental.categoryForm.arrivalCityPlaceholder')}
              disabled={isViewMode}
            />
          </div>
          <StyledTranslatableTextarea
            originalName={`oneWayFees.${index}.description`}
            control={control}
            title={t('carRental.categoryForm.oneWayFeeDescription')}
            name={`oneWayFees.${index}.description`}
            setValue={setValue}
            language={language}
            placeholder={t(
              'carRental.categoryForm.oneWayFeeDescriptionPlaceholder',
            )}
            rows={3}
            disabled={isViewMode}
          />
          <div className="flex flex-col gap-1">
            <Label className="text-gray-700">
              {t('carRental.categoryForm.amount')}
            </Label>
            <div className="flex gap-6 w-full items-center">
              <div className="min-w-20 max-w-40">
                <StyledInput
                  control={control}
                  name={`oneWayFees.${index}.amount`}
                  placeholder={t('carRental.categoryForm.amountPlaceholder')}
                  type="number"
                  min={0}
                  disabled={isViewMode}
                />
              </div>
            </div>
            <p className="text-sm text-gray-500">
              {t('carRental.globalFormDescription')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
